import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollEdges {
  /** Content is hidden above - the user has scrolled down. */
  top: boolean;
  /** Content is hidden below - there is more to scroll to. */
  bottom: boolean;
}

/**
 * Which edges of a scroll container have content past them, kept live as it
 * scrolls and resizes. For drawing an "there is more" shadow at that edge.
 *
 * A hook rather than the CSS-only `background-attachment: local` trick: that
 * technique hides its shadows under solid strips of the surface colour, which
 * only works on an opaque surface. The onboarding card is `bg-card/70` glass
 * with the lattice behind it, so those strips showed as visible bands.
 */
export function useScrollEdges<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<ScrollEdges>({
    top: false,
    bottom: false,
  });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1px slack: fractional scroll positions never quite reach the end.
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges(prev =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // The content changes height (a step's options load, the window resizes),
    // so the bottom edge can appear or vanish without a scroll event.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [measure]);

  return { ref, edges };
}
